import { useCallback } from "react";

import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";

import { useAuthState } from "./use-auth-state";
import { useRemoveSessionKey } from "./use-imported-session-keys";

/**
 * Returns a function that signs the user out using the correct cleanup path for
 * the current auth state. Both connected paths run the same post-logout cleanup
 * (clear subaccount selection, invalidate queries, clear local prefs) so the
 * app lands in the same state regardless of how the user was authed.
 *
 * - `WALLET_CONNECTED`: disconnects the wallet adapter via `disconnect()`
 *   (which includes the shared cleanup).
 * - `SESSION_KEY_IMPORTED`: removes the stored imported session key so it can't
 *   be rehydrated on next mount, then runs the shared cleanup.
 * - `LOADING` / `DISCONNECTED`: no-op.
 *
 * Prefer this over calling `disconnect()` or `removeSessionKey()` directly
 * anywhere a UI element treats both connected states as "logged in".
 */
export const useLogout = () => {
  const { state, subaccountAddress } = useAuthState();
  const { disconnect, cleanupAfterLogout } = useWriteDexSdk();
  const removeSessionKey = useRemoveSessionKey();

  return useCallback(() => {
    if (state === "SESSION_KEY_IMPORTED" && subaccountAddress) {
      removeSessionKey(subaccountAddress);
      cleanupAfterLogout();
      return;
    }
    if (state === "WALLET_CONNECTED") {
      disconnect();
    }
  }, [state, subaccountAddress, removeSessionKey, cleanupAfterLogout, disconnect]);
};
